import React, { useId } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { colors } from "../../../../theme";

/**
 * Pure SVG Mini Area / Sparkline Chart
 * Computes smooth bezier curves and area gradients using theme color tokens.
 */
const MiniAreaChart = ({
  data = [10, 15, 12, 22, 18, 25, 28, 24, 32],
  color = colors.charts.blue,
  height = 46,
  strokeWidth = 2,
}) => {
  const uniqueId = useId().replace(/:/g, "_");
  const gradientId = `sparkline-gradient-${uniqueId}`;

  const safeData = data && data.length > 0 ? data : [10, 15, 12, 22, 18, 25, 28, 24, 32];
  const width = 160;
  const svgHeight = 50;
  const padding = 4;

  const minVal = Math.min(...safeData);
  const maxVal = Math.max(...safeData);
  const range = maxVal - minVal || 1;

  // Map data points into SVG coordinate space
  const points = safeData.map((val, idx) => {
    const x = padding + (idx / (safeData.length - 1)) * (width - padding * 2);
    const y =
      svgHeight -
      padding -
      ((val - minVal) / range) * (svgHeight - padding * 2);
    return { x, y };
  });

  // Build smooth bezier curve path string
  const buildSmoothPath = (pts) => {
    if (pts.length === 0) return "";
    if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`;

    let path = `M ${pts[0].x} ${pts[0].y}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = i > 0 ? pts[i - 1] : pts[0];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = i !== pts.length - 2 ? pts[i + 2] : p2;

      const cp1x = p1.x + (p2.x - p0.x) / 6;
      const cp1y = p1.y + (p2.y - p0.y) / 6;
      const cp2x = p2.x - (p3.x - p1.x) / 6;
      const cp2y = p2.y - (p3.y - p1.y) / 6;

      path += ` C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(
        1
      )} ${cp2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
    }
    return path;
  };

  const linePath = buildSmoothPath(points);
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${svgHeight} L ${points[0].x} ${svgHeight} Z`;

  return (
    <Box
      sx={{
        width: "100%",
        height: height,
        overflow: "hidden",
        display: "flex",
        alignItems: "flex-end",
      }}
    >
      <svg
        viewBox={`0 0 ${width} ${svgHeight}`}
        preserveAspectRatio="none"
        style={{
          width: "100%",
          height: "100%",
          display: "block",
          overflow: "visible",
        }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.35} />
            <stop offset="70%" stopColor={color} stopOpacity={0.08} />
            <stop offset="100%" stopColor={color} stopOpacity={0.0} />
          </linearGradient>
        </defs>

        {/* Gradient Area Fill */}
        <path d={areaPath} fill={`url(#${gradientId})`} />

        {/* Smooth Line Stroke */}
        <path
          d={linePath}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Box>
  );
};

MiniAreaChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.number),
  color: PropTypes.string,
  height: PropTypes.number,
  strokeWidth: PropTypes.number,
};

export default MiniAreaChart;
